import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ShieldCheck, Star, MapPin, Search } from "lucide-react";
import Navbar from "@/components/Navbar";

export default function SearchPage() {
  const [sp, setSp] = useSearchParams();
  const [postcode, setPostcode] = useState(sp.get("postcode") || "");
  const [category, setCategory] = useState(sp.get("category") || "");
  const [categories, setCategories] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data)); }, []);

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const params = {};
      if (postcode) params.postcode = postcode;
      if (category) params.category = category;
      const { data } = await api.get("/providers", { params });
      setProviders(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchProviders(); }, []);

  const submit = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (postcode) p.set("postcode", postcode);
    if (category) p.set("category", category);
    setSp(p);
    fetchProviders();
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <form onSubmit={submit} className="card !p-3 flex flex-col sm:flex-row gap-3 items-stretch">
          <div className="flex items-center gap-3 flex-1 px-4 border-r border-slate-100">
            <Search className="text-slate-400" size={20} />
            <input data-testid="search-postcode" value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="Postcode e.g. L1" className="w-full bg-transparent py-3 outline-none" />
          </div>
          <select data-testid="search-category" value={category} onChange={(e) => setCategory(e.target.value)} className="bg-transparent px-4 py-3 outline-none">
            <option value="">All categories</option>
            {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <button data-testid="search-submit" className="btn-accent whitespace-nowrap">Search</button>
        </form>

        <div className="mt-8">
          <h2 className="font-display text-2xl font-bold">{loading ? "Searching…" : `${providers.length} pros available`}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
            {providers.map((p) => (
              <Link key={p.id} to={`/providers/${p.id}`} data-testid={`provider-card-${p.id}`} className="card hover:-translate-y-1 transition-transform">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-500 text-white inline-flex items-center justify-center font-bold text-lg">
                    {(p.business_name || p.owner_name || "?").slice(0, 1)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{p.business_name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-2">
                      <Star size={12} className="text-amber-500" /> {p.rating_avg?.toFixed?.(1) || "New"} · {p.review_count || 0} reviews
                    </div>
                  </div>
                  {p.verified && <span className="badge-verified"><ShieldCheck size={12} /> Verified</span>}
                </div>
                <p className="text-sm text-slate-600 mt-3 line-clamp-2">{p.bio || "Local expert ready to help."}</p>
                <div className="mt-4 flex items-center justify-between text-sm">
                  <span className="text-slate-600 inline-flex items-center gap-1"><MapPin size={14} /> {(p.postcodes || []).slice(0,3).join(", ") || "Liverpool"}</span>
                  <span className="font-semibold text-slate-900">{p.hourly_rate ? `£${p.hourly_rate}/hr` : "Custom quote"}</span>
                </div>
              </Link>
            ))}
            {!loading && providers.length === 0 && (
              <div className="col-span-full card text-center text-slate-600">No providers found. Try a different postcode.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
